/**
 * ScreenContainer Component
 *
 * Reusable container that renders appropriate UI based on screen state.
 * Handles loading, success, error, empty, and in-progress states.
 *
 * @example
 * ```typescript
 * <ScreenContainer
 *   state={screen.state}
 *   error={screen.error}
 *   onRetry={screen.retry}
 *   skeleton={{ type: 'dashboard' }}
 *   emptyState={{ title: 'No data', description: 'Get started by adding items' }}
 * >
 *   <YourContent />
 * </ScreenContainer>
 * ```
 */

import React from 'react';
import {
  ScreenState,
  ScreenError,
  SkeletonConfig,
  EmptyStateConfig,
  ErrorStateConfig,
} from '@/types/screen.types';
import { FadeIn } from '@/components/AnimatedElements';
import {
  CardSkeleton,
  ListSkeleton,
  TableSkeleton,
  FormSkeleton,
  DashboardSkeleton,
  KanbanSkeleton,
} from './LoadingSkeletons';
import { DefaultErrorState } from './ErrorStates';
import { DefaultEmptyState } from './EmptyStates';
import { useThemeColors } from '@/styles';

export interface ScreenContainerProps {
  /** Current screen state */
  state: ScreenState;
  /** Error object (required if state is ERROR) */
  error?: ScreenError | null;
  /** Retry function for error states */
  onRetry?: () => void;
  /** Loading skeleton configuration */
  skeleton?: SkeletonConfig;
  /** Empty state configuration */
  emptyState?: EmptyStateConfig;
  /** Error state configuration */
  errorState?: ErrorStateConfig;
  /** Content to render in SUCCESS state */
  children: React.ReactNode;
  /** Whether to show loading overlay on success content during IN_PROGRESS */
  showProgressOverlay?: boolean;
}

/**
 * Container component that manages screen state rendering.
 *
 * Automatically renders:
 * - Loading skeletons during INITIALIZING/LOADING
 * - Error states with retry button during ERROR
 * - Empty states during EMPTY
 * - Success content during SUCCESS
 * - Optional loading overlay during IN_PROGRESS
 */
export function ScreenContainer({
  state,
  error,
  onRetry,
  skeleton,
  emptyState,
  errorState,
  children,
  showProgressOverlay = false,
}: ScreenContainerProps) {
  const colors = useThemeColors();
  // Render loading skeleton
  if (state === ScreenState.INITIALIZING || state === ScreenState.LOADING) {
    return (
      <FadeIn direction="up" delay={0} duration={0.3}>
        {renderSkeleton(skeleton)}
      </FadeIn>
    );
  }

  // Render error state
  if (state === ScreenState.ERROR && error) {
    return (
      <FadeIn direction="up" delay={0} duration={0.3}>
        <DefaultErrorState
          error={error}
          onRetry={onRetry}
          config={errorState}
        />
      </FadeIn>
    );
  }

  // Render empty state
  if (state === ScreenState.EMPTY && emptyState) {
    return (
      <FadeIn direction="up" delay={0} duration={0.3}>
        <DefaultEmptyState config={emptyState} />
      </FadeIn>
    );
  }

  // Render success content (with optional progress overlay)
  if (
    state === ScreenState.SUCCESS ||
    state === ScreenState.IN_PROGRESS ||
    state === ScreenState.IDLE
  ) {
    return (
      <div className="relative h-full">
        {children}
        {state === ScreenState.IN_PROGRESS && showProgressOverlay && (
          <div
            className="absolute inset-0 backdrop-blur-sm flex items-center justify-center z-50"
            style={{ backgroundColor: `${colors.background}80` }}
          >
            <div
              className="rounded-lg shadow-lg p-6"
              style={{ backgroundColor: colors.offWhite }}
            >
              <div className="flex items-center space-x-3">
                <div
                  className="animate-spin rounded-full h-6 w-6 border-b-2"
                  style={{ borderColor: colors.primary }}
                />
                <span
                  className="font-medium"
                  style={{ color: colors.text.primary }}
                >
                  Processing...
                </span>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Fallback: render children
  return <>{children}</>;
}

/**
 * Render appropriate skeleton based on configuration
 */
function renderSkeleton(config?: SkeletonConfig) {
  if (!config) {
    return <CardSkeleton count={1} />;
  }

  if (config.CustomSkeleton) {
    return <config.CustomSkeleton />;
  }

  const count = config.count || 1;

  switch (config.type) {
    case 'card':
      return <CardSkeleton count={count} />;
    case 'list':
      return <ListSkeleton count={count} />;
    case 'table':
      return <TableSkeleton rows={count} />;
    case 'form':
      return <FormSkeleton fields={count} />;
    case 'dashboard':
      return <DashboardSkeleton />;
    case 'kanban':
      return <KanbanSkeleton />;
    default:
      return <CardSkeleton count={count} />;
  }
}
